import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { X, CreditCard, Shield, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (consentDate: string) => void;
}

export function PaymentModal({ isOpen, onClose, onSuccess }: PaymentModalProps) {
  const [stripe, setStripe] = useState<any>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const initStripe = async () => {
        const stripeInstance = await loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);
        if (stripeInstance) {
          const elements = stripeInstance.elements();
          const card = elements.create('card', {
            style: {
              base: {
                fontSize: '16px',
                color: '#32325d',
                fontFamily: '"Inter", sans-serif',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#fa755a',
                iconColor: '#fa755a',
              },
            },
          });

          card.mount('#card-element');
          card.on('change', (event: any) => {
            setError(event.error ? event.error.message : '');
          });

          setStripe(stripeInstance);
          setCardElement(card);
        }
      };

      initStripe();

      return () => {
        if (cardElement) {
          cardElement.destroy();
        }
      };
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !cardElement || !agreeToTerms) return;

    setProcessing(true);
    setError(null);

    try {
      // Create payment method instead of token
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        setError(pmError.message);
        return;
      }

      // Save payment method ID to user settings via Supabase
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Not authenticated');
        return;
      }

      const { error: updateError } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          stripe_payment_method_id: paymentMethod.id,
        });

      if (updateError) {
        setError('Failed to save payment method');
        return;
      }

      // Pass the consent date to the parent component
      const consentDate = new Date().toISOString();
      onSuccess(consentDate);
      onClose();

    } catch (err) {
      setError('Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 relative overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-white">Automatyczne uzupełnianie kredytów</h2>
              <p className="text-blue-100 mt-1">Bezpieczna płatność kartą</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-4 bg-white/10 rounded-lg p-3 text-white/90">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              <span className="text-lg font-semibold">199,00 zł</span>
            </div>
            <p className="text-sm text-white/80 mt-1">
              5000 kredytów gdy saldo spadnie poniżej 100
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dane karty płatniczej
              </label>
              <div
                id="card-element"
                className="border rounded-lg p-4 bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <div className="flex items-start gap-3 bg-blue-50 p-4 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Bezpieczna płatność</p>
                <p className="mt-1">Twoje dane są szyfrowane i bezpieczne. Nigdy nie przechowujemy pełnych danych karty.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex items-center h-5 mt-1">
                <input
                  id="terms"
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              <div>
                <label htmlFor="terms" className="text-sm text-gray-700">
                  Wyrażam zgodę na automatyczne obciążanie karty kwotą 199 zł gdy liczba kredytów spadnie poniżej 100
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  Możesz anulować zgodę w każdej chwili w ustawieniach konta
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2 text-gray-500">
              <Info className="w-4 h-4" />
              <span className="text-sm">Płatność obsługuje Stripe</span>
            </div>
            <button
              type="submit"
              disabled={!agreeToTerms || processing}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Przetwarzanie...</span>
                </div>
              ) : (
                'Zapłać 199,00 zł'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}